import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import SDFormWrapper from '../SDFormTemplate/SDFormWrapper';
import Create from './SDForm/FormRows/Create';
import Groups from './SDForm/FormRows/Groups/Groups';
import Sites from './SDForm/FormRows/Sites';
import CustomFields from './SDForm/FormRows/CustomFields';

import { getCompanyIdFromRoute } from 'services/redux/selectors/company';
import PriorityEvents from './SDForm/FormRows/PriorityEvents';
import Reports from './SDForm/FormRows/Reports';

//images
import domainImage from './SideBarContent/domain_image.png';
import logoImage from './SideBarContent/logo_image.png';
import groupsImage from './SideBarContent/groups_image.png';
import sitesImage1 from './SideBarContent/sites_image_1.png';
import sitesImage2 from './SideBarContent/sites_image_2.png';
import sitesImage3 from './SideBarContent/sites_image_3.png';
import customFieldsImage from './SideBarContent/custom_fields_image.png';
import reportsImage from './SideBarContent/reports_image.png';

function CreateEnterprise({ showModal, handleClose, handleLoadCompanyStart }) {

  const [submitLoading, setSubmitLoading] = useState(false);

  const [invalid, setInvalid] = useState(true);
  const [invalidConditions, setInvalidConditions] = useState({});
  const [availableGroups, setAvailableGroups] = useState([])

  const companyId = useSelector(getCompanyIdFromRoute);

  useEffect(() => {
    setInvalid(Object.values(invalidConditions).filter(c => c.condition).some(Boolean))
  }, [invalidConditions])

  const title = 'Create New Enterprise';

  return (
    <>
      <SDFormWrapper
        key='sdformwrapper_1'
        showModal={showModal}
        handleClose={handleClose}
        title={title}
        modalSize='lg'
        initialStateConditions={[{ field: 'reports_group_setting', value: 'root' }]}
        submitForm={{
          endpoint: `/ServiceDesk/createEnterprise/${companyId}`,
          requestType: 'post',
          additionalParams: {},
          triggerOnSubmit: handleLoadCompanyStart,
        }}
        setSubmitLoading={setSubmitLoading}
        rowContent={[
          {component: Create, id: 'create', props: {setInvalidConditions: setInvalidConditions }},
          {
            component: Groups,
            id: 'groups',
            props: {
              setInvalidConditions: setInvalidConditions,
              setAvailableGroups: setAvailableGroups
            }},
          {
            component: Sites,
            id: 'sites',
            props: {
              setInvalidConditions: setInvalidConditions,
              setAvailableGroups: setAvailableGroups,
              groups: availableGroups,
            }},
          {
            component: CustomFields,
            id: 'custom_fields',
            props: {
              setInvalidConditions: setInvalidConditions,
            }},
          {
            component: PriorityEvents,
            id: 'priority_events',
            props: {
              setInvalidConditions: setInvalidConditions,
            }},
          {
            component: Reports,
            id: 'reports',
            props: {
              setInvalidConditions: setInvalidConditions,
            }},
        ]}

        enableSidePane={true}

        sidePaneContent = {[
          // createEnterprise - subpage
          {
            key: 'gettingStarted',
            name: 'Getting Started',
            content: [
              {type: 'header' , body: 'Getting Started'},
              {type: 'subHeader', body: 'Account Type'},
              {type: 'message', body: `You can define if its an internal-focused account called **Installer View**, 
              for running reports and compliance, or creating a customer-facing **Enterprise** account for alarms, 
              notifications, reporting and more.`},
              // {type: 'divider', body: ''},
              {type: 'subHeader', body: 'Domain Restriction'},
              {type: 'message', body: `Limit invitations to the platform to a select number of email domains. 
                For multiple email domains, put a space between each domain as shown.`
              },
              {type: 'imageSrc', body: domainImage},
              {type: 'divider', body: ''},
              {type: 'subHeader', body: 'Logo'},
              {type: 'message', body: `Use a company logo to unify your, or the customer's branding, across the platform. 
                File format JPEG or PNG, most logo ratios will fit as standard.`
              },
              {type: 'imageSrc', body: logoImage},
            ],
          },
          // groups - subpage
          {
            key: 'groupsSideBar',
            name: 'Groups',
            content: [
              {type: 'header' , body: 'Groups'},
              {type: 'message', body: `Groups are like the folder structure on your PC, 
                allowing you to organise your sites by region, 
                signalling type - really whatever fits for you or your customer's business.`},
              {type: 'imageSrc', body: groupsImage},
              {type: 'message', body: `Use the **Add** buttons to create groups, drag and drop to shuffle them around, to create your grouping structure.`},
            ],
          },
          // sites - subpage
          {
            key: 'sitesSideBar',
            name: 'Sites',
            content: [
              {type: 'header' , body: 'Sites'},
              {type: 'message', body: `Add sites from your Service Desk account directly into the Enterprise you're creating. 
                Use all the site field filters to narrow down your search, and use the checkboxes to select your sites.`
              },
              {type: 'imageSrc', body: sitesImage1},
              {type: 'message', body: `Then use the big **Plus** button to assign the sites to one or more of your groups.`},
              {type: 'imageSrc', body: sitesImage2},
              {type: 'message', body: `Use the **dropdown** menu to select the group you wish to drop your sites into.`},
              {type: 'imageSrc', body: sitesImage3},
            ],
          },
          // custom fields - subpage
          {
            key: 'customFieldsSideBar',
            name: 'Custom Fields',
            content: [
              {type: 'header' , body: 'Custom Fields'},
              {type: 'message', body: `Custom fields enable users to view and manage their own data fields in CONXTD. 
                These are typically used for Contract Numbers or Store IDs, and are shown against every site in the Enterprise account.`
              },
              {type: 'imageSrc', body: customFieldsImage},
            ],
          },
          {
            key: 'priorityEventsSideBar',
            name: 'Priority Events',
            content: [
              {type: 'header' , body: 'Priority Events'},
              {type: 'message', body: `Our fully cloud-based monitoring solution can be configured with a few options`},
              {type: 'imageSrc', body: customFieldsImage},
              {type: 'markdown', body: `| Type | Description |
|------|-------------|
| Live | Enables Priority Events for users to get live updates on alarm activity, but no access to SOPs. |
| Live - SOP | Everything in Live, plus access to SOPs, SOP configuration, and the Completed Actions report. |
| Live - SOP - Sounds | Everything in Live - SOP, but audio/visual notifications only mute upon acknowledgement from an Operator. |
| Live - SOP - Sounds - Auto Unmute | Everything in Live - SOP - Sounds, but audio/visual notifications automatically unmute after 15 minutes. |
`},
            ],
          },
          {
            key: 'reportsSideBar',
            name: 'Reports',
            content: [
              {type: 'header' , body: 'Reports'},
              {type: 'message', body: `Enable our fully automated compliance reporting tools for users on the top level dashboard only, 
                or for every group that is created in the Enterprise (**most popular**).`
              },
              {type: 'imageSrc', body: reportsImage},
              {type: 'message', body: "Here's a list of our reports to choose from."},
              {type: 'markdown', body: `| Report Type | Description |
|------------|-------------|
| Open | Tracks opening or disarming of sites live and provides early, late, on-time, and failed-to-open summaries. |
| Close | Tracks closing or arming of sites live and provides early, late, on-time, and failed-to-close summaries. |
| Overnight Activity | Displays all alarm activity during a defined period between arming and disarming of your sites. |
| Suspicious | Shows irregular opening or disarming of sites after the scheduled close has taken place. |
| Fire Test | Based on site testing schedules, shows each site's compliance with the testing schedule, and identifies those that have failed to test during the defined testing window. |
| Panic Test | Similar to the Fire Test report, shows each site's compliance with the testing schedule, and identifies those that have failed to test during the defined testing window. |
| Outstanding | Shows all alarms, fault conditions, and bypasses currently outstanding or active on your Enterprise. |
| Keyholders Report | Delivers a summary of keyholder and contact creations, amendments, and deletions to a designated email address. |
| Open & Close by Area | Provides an in-depth breakdown of the status of each area currently active in the system. |

`},

            ],
          },
          // --------------------------|
        ]}

        footerProps={{
          invalid: invalid,
          invalidTooltipDescriptions: Object.values(invalidConditions).filter(c => c?.condition),
          disabledButton: true,
          submitForm: {
            endpoint: `/ServiceDesk/${companyId}`,
            requestType: 'post',
            additionalParams: {},
          },
          submitLoading: submitLoading,
          title: title,
        }}
      />
    </>    
  )
}

export default CreateEnterprise
